/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/jsx-no-duplicate-props */
import cx from 'classnames';
import Input from 'components/input/Input';
import Typography from 'components/typography/Typography';
import { TYPOGRAPHY_VARIANTS } from 'components/typography/typography-utils';
import { I18nNamespace } from 'constants/i18n.const';
import { useAppContext } from 'context/AppContext';
import { useSoundContext } from 'context/SoundContext';
import { useModal } from 'hooks/useModal';
import { useTranslation } from 'hooks/useTranslation';
import { get, toNumber } from 'lodash';
import { ApplyVoucherCodeResponseModel } from 'models/photo/bill-acceptor.model';
import {
  VOUCHER_STATUS,
  VOUCHER_STATUS_MESSAGE_MAPPING,
} from 'models/photo/transaction.model';
import React, { FC, HTMLAttributes, memo, useEffect, useState } from 'react';
import { useApplyVoucherCodeApiActionMutation } from 'store/api/payment.slice-api';
import { Keyboard } from '../../../components/keyboard/Keyboard';
import './keyboard-drawer.css';
import { useCheckPayment } from './useCheckPayment';

export type KeyboardDrawerDrawerProps = HTMLAttributes<HTMLDivElement> & {
  onOk?: (code: string, value: number) => void;
  onClose?: () => void;
  open: boolean;
};

const KeyboardDrawer: FC<KeyboardDrawerDrawerProps> = ({
  onOk,
  onClose,
  open,
  ...props
}) => {
  const { t, T } = useTranslation(I18nNamespace.COMMON);
  const [applyVoucherCodeApiAction] = useApplyVoucherCodeApiActionMutation();
  const { openErrorModal } = useModal();
  const { audio } = useSoundContext();
  const { isOnline } = useAppContext();
  const { costOfPayment } = useCheckPayment();

  const [code, setCode] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isApplyingCode, setIsApplyingCode] = useState<boolean>(false);

  const handleOnChange = (code: string) => {
    setErrorMessage?.('');
    setCode(code);
  };

  const handleSubmit = async () => {
    if (!code) return;
    setIsApplyingCode(true);
    setErrorMessage('');
    if (!isOnline) {
      setIsApplyingCode(false);
      openErrorModal(t('page:cannotUseVoucherByNetworkError'));
      return;
    }
    try {
      const res: ApplyVoucherCodeResponseModel =
        await applyVoucherCodeApiAction({
          code,
          price: costOfPayment,
        }).unwrap();
      audio?.playVoucherApplied?.();
      onOk?.(code, toNumber(res?.data?.amount) || 0);
      onClose?.();
    } catch (error) {
      audio?.playVoucherExpired?.();
      const errorCode = get(error as any, 'data.code') as VOUCHER_STATUS;
      setErrorMessage(
        T(VOUCHER_STATUS_MESSAGE_MAPPING[errorCode]) ||
          T(`${I18nNamespace.COMMON}:commonError`),
      );
    } finally {
      setIsApplyingCode(false);
    }
  };

  useEffect(() => {
    if (open) {
      setCode('');
      setErrorMessage('');
      setIsApplyingCode(false);
    }
  }, [open]);

  return (
    <div
      className={cx('pb-keyboardDrawer-container', {
        'pb-keyboardDrawer-container-open': open,
      })}
    >
      <div className={cx('pb-keyboardDrawer-backdrop')} onClick={onClose} />
      <div
        {...props}
        className={cx('pb-keyboardDrawer', props?.className)}
        onClick={(e) => e.preventDefault()}
      >
        <div className="pb-keyboardDrawer-input-wrapper">
          <Input
            className="pb-keyboardDrawer-input"
            value={code}
            placeholder={`${t('common:voucherCode')}`}
            isError={!!errorMessage}
            variantFill
            readOnly
          />
        </div>
        {!!errorMessage && (
          <Typography
            className="voucher-code-error-text"
            variant={TYPOGRAPHY_VARIANTS.BODY}
            firstCapCase
            isError
          >
            {errorMessage}
          </Typography>
        )}
        <Keyboard
          value={code}
          loading={isApplyingCode}
          handleChange={handleOnChange}
          handleEnter={handleSubmit}
          isNumeric
        />
      </div>
    </div>
  );
};

export default memo(KeyboardDrawer);
